import { useState, useEffect } from "react";
import { AuthPageHeader } from "./components/AuthPageHeader";
import { AuthPagePaper } from "./components/AuthPagePaper";
import { AuthPageFooter } from "./components/AuthPageFooter";
import ReCAPTCHA from "react-google-recaptcha";
import { animatePresenceConfig, appConfig } from "../../../config/appConfig";
import { AnimatePresence, motion } from "framer-motion";
import { MessageBox } from "../../components/MessageBox/MessageBox";
import { userApi } from "../../../api/userApi";
import { Loader } from "../../components/Loader/Loader";
import { linksConstants } from "../../../constants/linksConstants";
import { serializeError } from "../../../utils/serializeError";
import InputField from "@demystdata/ui/input_field";
import Button from "@demystdata/ui/button";
import Link from "next/link";

export const ForgotPassword = () => {
  // "IDLE", "LOADING", "SUCCESS", "FAILED"
  const [status, setStatus] = useState("IDLE");
  const [error, setError] = useState(null);

  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const [captchaResponse, setCaptchaResponse] = useState(null);

  const submitButtonDisabled = status === "LOADING" || (isCaptchaRequired && !captchaResponse);

  const onCaptchaChange = captchaResponse => {
    setCaptchaResponse(captchaResponse);
  };

  const checkIfCaptchaIsRequired = async () => {
    try {
      const {
        data: { next_submit_requires_captcha },
      } = await userApi.isForgotPasswordCaptchaNeeded();

      if (next_submit_requires_captcha) {
        setIsCaptchaRequired(true);
      }
    } catch (e) {
      console.error(e);
      // TODO: LogRocket info?
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      setStatus("LOADING");
      setError(null);
      const form = new FormData(event.target);
      const email = form.get("email");

      await userApi.sendResetPasswordEmail(email, captchaResponse);
      setStatus("SUCCESS");
    } catch (e) {
      setStatus("FAILED");
      setError(serializeError(e));
      await checkIfCaptchaIsRequired();
    }
  };

  useEffect(() => {
    checkIfCaptchaIsRequired();
  }, []);

  return (
    <main role="main" className="ForgotPassword">
      <div className="ForgotPassword__header">
        <AuthPageHeader />
      </div>
      <div className="ForgotPassword__paper">
        <AuthPagePaper>
          <header className="ForgotPassword__paper__header">
            <h1 className="ForgotPassword__paper__header__title">
              {status !== "SUCCESS"
                ? "Forgot your password?"
                : "Check your email for instructions to reset your password!"}
            </h1>
            <div className="ForgotPassword__paper__header__message">
              {status === "SUCCESS" && <p>Didn't get the email? Check your spam folder.</p>}
              {status === "IDLE" && (
                <p>
                  Enter the email address associated with your account and we'll send you a link to
                  reset your password.
                </p>
              )}
            </div>
          </header>
          {status !== "SUCCESS" && (
            <form
              onSubmit={handleSubmit}
              className="ForgotPasswordForm"
              data-test-id="ForgotPasswordForm"
            >
              <InputField
                className="ForgotPasswordForm__email"
                size="regular"
                type="email"
                name="email"
                label="Email"
                id="email"
                required
              />
              {isCaptchaRequired && (
                <div className="ForgotPasswordForm__captchaWrapper">
                  <ReCAPTCHA sitekey={appConfig.CAPTCHA_KEY} onChange={onCaptchaChange} />
                </div>
              )}
              <AnimatePresence>
                {status === "FAILED" && error && (
                  <motion.div
                    className="ForgotPasswordForm__errorMessageWrapper"
                    {...animatePresenceConfig}
                  >
                    <MessageBox>{error.message}</MessageBox>
                  </motion.div>
                )}
              </AnimatePresence>
              <Button
                className="ForgotPasswordForm__submitButton"
                data-test-id="ForgotPasswordSubmit"
                type="submit"
                size="large"
                variant="primary"
                disabled={submitButtonDisabled}
                fullWidth
              >
                Continue
              </Button>
            </form>
          )}
          <div className="ForgotPassword__paper__backLinks">
            <Link href={linksConstants.LOGIN} data-test-id="BackToLogin">
              Back to login
            </Link>
          </div>
          <AnimatePresence>
            {status === "LOADING" && (
              <motion.div
                className="ForgotPassword__paper__loadingOverlay"
                {...animatePresenceConfig}
              >
                <Loader />
              </motion.div>
            )}
          </AnimatePresence>
        </AuthPagePaper>
      </div>
      <div className="ForgotPassword__footer">
        <AuthPageFooter />
      </div>
    </main>
  );
};
